/**
 * SeveranceSkillEffect — Conjuration Tether path upgrade: psychic snap AoE.
 *
 * Upgrade node (detach_upgrade_2), parent: detach_upgrade_1 (Clean Break).
 * When the detached shade is destroyed in combat, the psychic snap disrupts
 * all entities within 2 tiles — 0.3-second stagger and 10% movement slow
 * for 3 seconds.
 *
 * The integration layer calls `onDetachedShadeDestroyed(x, y)` with the
 * shade's death position. Severance queries UpgradeEffectResolver for its
 * AoE parameters, finds enemies in radius, and applies stagger + slow.
 *
 * Stagger respects the shared CC immunity window (500ms after expiry per
 * ADR-065). Slow applies independently as a debuff, not gated by CC immunity.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see conjuration-spec.md § Severance (detach_upgrade_2)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SeveranceDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly enemies: {
    /** Return all enemy IDs within radiusTiles of (x, y). */
    getInRadius(x: number, y: number, radiusTiles: number): ReadonlyArray<{ id: string }>;
  };
  readonly statusEffects: {
    /** Apply a status effect to a specific target. Returns true if applied. */
    applyToTarget(targetId: string, statusId: string, opts: {
      durationMs: number;
      intensity?: number;
      sourceSkillId: string;
    }): boolean;
    /** Check if a target is in a CC immunity window for the given effect. */
    isTargetImmune(targetId: string, statusId: string): boolean;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface SeveranceConfig {
  /** Stagger duration in ms applied on shade death. */
  readonly severanceStaggerMs: number;
  /** Movement slow intensity (0–1, where 0.1 = 10% reduction). */
  readonly severanceSlowPercent: number;
  /** Movement slow duration in ms. */
  readonly severanceSlowDurationMs: number;
  /** AoE radius in tiles from shade death position. */
  readonly severanceRadiusTiles: number;
}

const DEFAULT_CONFIG: SeveranceConfig = {
  severanceStaggerMs: 300,
  severanceSlowPercent: 0.1,
  severanceSlowDurationMs: 3000,
  severanceRadiusTiles: 2,
};

/** Resolver scope — 'detach' for upgrades with enhancesId: 'detach'. */
const RESOLVER_SCOPE = 'detach';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SeveranceSkillEffect implements SkillEffect {
  readonly skillId = 'detach_upgrade_2';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SeveranceDeps;
  private readonly config: SeveranceConfig;

  // Observable state for scenarios and integration layer
  private _procCount = 0;
  private _lastAffectedCount = 0;
  private _totalStaggersApplied = 0;
  private _totalSlowsApplied = 0;
  private _totalStaggersBlocked = 0;

  constructor(deps: SeveranceDeps, config: Partial<SeveranceConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Severance-specific API ---

  /**
   * Called by integration layer when the detached shade is destroyed in combat.
   * Applies stagger + slow to all enemies within radius of the shade's
   * death position.
   *
   * @param x  Shade death position X (world pixels)
   * @param y  Shade death position Y (world pixels)
   */
  onDetachedShadeDestroyed(x: number, y: number): void {
    const radiusTiles = this.getResolvedRadius();
    const staggerMs = this.getResolvedStaggerMs();
    const slowDurationMs = this.getResolvedSlowDurationMs();
    const slowIntensity = this.getResolvedSlowPercent();

    const enemies = this.deps.enemies.getInRadius(x, y, radiusTiles);
    let affectedCount = 0;

    for (const enemy of enemies) {
      // Stagger: respects CC immunity window (ADR-065)
      const isStaggerImmune = this.deps.statusEffects.isTargetImmune(enemy.id, 'stagger');
      if (!isStaggerImmune) {
        this.deps.statusEffects.applyToTarget(enemy.id, 'stagger', {
          durationMs: staggerMs,
          sourceSkillId: 'detach_upgrade_2',
        });
        this._totalStaggersApplied++;
      } else {
        this._totalStaggersBlocked++;
      }

      // Slow: independent debuff, not gated by CC immunity
      this.deps.statusEffects.applyToTarget(enemy.id, 'speed_slow', {
        durationMs: slowDurationMs,
        intensity: slowIntensity,
        sourceSkillId: 'detach_upgrade_2',
      });
      this._totalSlowsApplied++;

      affectedCount++;
    }

    this._lastAffectedCount = affectedCount;
    this._procCount++;
  }

  // --- Resolved values ---

  getResolvedStaggerMs(): number {
    const resolved = this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'severanceStaggerMs');
    return resolved || this.config.severanceStaggerMs;
  }

  getResolvedSlowPercent(): number {
    const resolved = this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'severanceSlowPercent');
    return resolved || this.config.severanceSlowPercent;
  }

  getResolvedSlowDurationMs(): number {
    const resolved = this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'severanceSlowDurationMs');
    return resolved || this.config.severanceSlowDurationMs;
  }

  getResolvedRadius(): number {
    const resolved = this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'severanceRadiusTiles');
    return resolved || this.config.severanceRadiusTiles;
  }

  // --- State getters ---

  get procCount(): number { return this._procCount; }
  get lastAffectedCount(): number { return this._lastAffectedCount; }
  get totalStaggersApplied(): number { return this._totalStaggersApplied; }
  get totalSlowsApplied(): number { return this._totalSlowsApplied; }
  get totalStaggersBlocked(): number { return this._totalStaggersBlocked; }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  isActive(): boolean { return false; }
  get cooldown(): number { return 0; }
  update(_delta: number): void {}
  destroy(): void {}
}
